/**
 * Voices and the recordings that stand in for them: how a synthesized line is
 * shaped, which lines have a real recording behind them and how it is trimmed,
 * and the delays before somebody reacts to something.
 *
 * The delays are the load-bearing part. A shout that lands on the same frame
 * as the thing it is about reads as a sound effect; a beat later it reads as
 * a person.
 */

/**
 * How long after the pane goes before Kimmo has an opinion about it.
 *
 * Presentation only - it decides when a sound plays and nothing else, so it is
 * not hashed. The number is the point though: fired on the same step, the yell
 * and the smash pile into each other and the ear reads one loud noise of no
 * particular kind. A beat behind, they read as cause and effect, which is the
 * whole joke - he goes through the window, *then* finds out what that cost.
 *
 * Short enough to still be the same event: much past a fifth of a second and
 * he is a man yelling about something that happened to him earlier.
 */
export const GLASS_SHOUT_DELAY = 0.18;

/**
 * The power-off sound ends at 1.97 s; the rest of its 2.502 s file is silence.
 * Start Kimmo's reaction right after the audible cut, skipping his silent lead-in.
 * Presentation only; this does not affect physics or the replay fingerprint.
 */
export const BLACKOUT_OHO_DELAY = 2.0;

/** The tent replies after Kimmo's trimmed recording (under 0.95 s). Presentation only. */
export const BLACKOUT_SHOUT_DELAY = BLACKOUT_OHO_DELAY + 0.95;

export interface SampleTrim {
  /** Multiplied into the cue's usual gain. 1 = plays as recorded. */
  gain?: number;
  /**
   * Seconds skipped at the head of the file.
   *
   * MP3 is not sample-accurate. The encoder prepends a padding frame, so a
   * decoded mp3 begins with roughly 10-30 ms of silence that no audio editor
   * shows you, and a percussive cue - a footstep, a swipe - lands that late.
   * For a shouted line nobody can hear it; for a hit that has to sit on the
   * frame it fires, dial the delay out here, or record in wav/ogg/opus, which
   * have no such padding.
   */
  trimStart?: number;
}

/**
 * Levels for recorded material. Files themselves are found by name - see
 * src/audio/samples.ts - so nothing needs registering here; this is only for
 * the takes that come back too hot, too quiet or a hair late.
 */
export const SAMPLES = {
  /** Trim across every recording at once, without touching the synth mix. */
  master: 1.0,
  /**
   * Level for a spoken line that is a recording rather than TTS. A real mouth
   * on a real microphone is louder than the browser's voice at the same
   * number, and the per-line `volume` (which fakes distance) still applies.
   */
  voiceGain: 0.9,
  /** Per-cue overrides, keyed like `hannu-swipe` or `voice:hannu`. */
  per: {
    // Speech starts at 0.89 s; retain a short lead-in without the recorded pause.
    'kimmo-oho': { trimStart: 0.87 },
  } as Record<string, SampleTrim>,
};

/**
 * Seed for the audio-only PRNG. Footstep pitch and Hannu's bubbles vary, but
 * they vary *identically* on every run - the same replay sounds the same.
 */
export const AUDIO_SEED = 0x1a71aa30;

/** Per-line delivery. Everything omitted falls back to `VOICE.base`. */
export interface VoiceStyle {
  /** 0.1 - 10, where 1 is the engine's normal speaking rate. */
  rate?: number;
  /** 0 - 2. Below 1 is a lower voice. */
  pitch?: number;
  /** 0 - 1. Distance is faked with this, since TTS has no 3D position. */
  volume?: number;
}

export const VOICE = {
  /** Preferred TTS languages, best first. Falls back to the browser default. */
  languages: ['fi-FI', 'fi'] as readonly string[],
  /** Slightly quick and slightly low: everybody here is shouting, not reading. */
  base: { rate: 1.05, pitch: 0.95, volume: 1.0 } satisfies Required<VoiceStyle>,
} as const;
